import type { ReactNode } from 'react'
import {
  ActivityIcon, AppsIcon, BoardIcon, ChevronIcon, FlowIcon, FolderIcon, GearIcon, GenieMark, HomeIcon, KeyboardIcon,
  LibraryIcon, MessageIcon, PanelLeftIcon, PlusIcon, SlidersIcon, UndoIcon, WalletIcon, XIcon,
} from '../ProductFrameIcons'

export type StudioMode = 'chat' | 'board' | 'flow'

const MODES = [
  { id: 'chat', Icon: MessageIcon },
  { id: 'board', Icon: BoardIcon },
  { id: 'flow', Icon: FlowIcon },
] as const

const iconBtn = 'flex size-7 items-center justify-center rounded-md text-muted'

/** Window chrome of the desktop app (0.1.6): session tabs, mode switch, panels, status bar. */
export function StudioShell({ mode, count, children }: { mode: StudioMode; count: string; children: ReactNode }) {
  return (
    <div className="flex h-full flex-col bg-card text-bone">
      <div className="flex h-10 flex-none items-center gap-1.5 border-b border-line px-3 text-[12px]">
        {/* macOS window controls sit in the space the app reserves at the left of its top bar. */}
        <span className="mr-3 flex items-center gap-2" aria-hidden>
          <span className="size-3 rounded-full border border-black/10 bg-[#ff5f57]" />
          <span className="size-3 rounded-full border border-black/10 bg-[#febc2e]" />
          <span className="size-3 rounded-full border border-black/10 bg-[#28c840]" />
        </span>
        <span className="flex items-center gap-1 rounded-md px-1.5 py-1">
          <GenieMark />
          <ChevronIcon width={9} height={9} className="text-faint" />
        </span>
        <span className={iconBtn}>
          <HomeIcon />
        </span>
        <span className="flex h-7 items-center gap-2 rounded-md bg-panel px-3">
          SS27 lookbook
          <XIcon width={11} height={11} className="text-faint" />
        </span>
        <span className={iconBtn}>
          <PlusIcon />
        </span>
        <span className="ml-auto flex items-center gap-0.5 rounded-lg bg-panel p-0.5">
          {MODES.map(({ id, Icon }) => (
            <span
              key={id}
              className={`flex h-6 w-8 items-center justify-center rounded-md ${id === mode ? 'bg-card text-bone shadow-[0_1px_2px_rgba(0,0,0,0.12)]' : 'text-faint'}`}
            >
              <Icon />
            </span>
          ))}
        </span>
        <span className="mx-1 h-4 w-px bg-line" />
        {mode !== 'chat' ? (
          <span className={`${iconBtn} ${mode === 'board' ? 'bg-panel text-bone' : ''}`}>
            <PanelLeftIcon />
          </span>
        ) : null}
        <span className={iconBtn}>
          <FolderIcon />
        </span>
        <span className={iconBtn}>
          <LibraryIcon />
        </span>
        <span className={`${iconBtn} relative`}>
          <ActivityIcon />
          <span className="absolute top-1 right-1 size-1.5 rounded-full bg-lamp" />
        </span>
        {mode === 'chat' ? (
          <span className={`${iconBtn} bg-panel text-bone`}>
            <AppsIcon />
          </span>
        ) : null}
        {mode === 'flow' ? (
          <span className={iconBtn}>
            <SlidersIcon />
          </span>
        ) : null}
        <span className={iconBtn}>
          <MessageIcon />
        </span>
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden">{children}</div>

      <div className="flex h-7 flex-none items-center gap-3 border-t border-line px-3 text-[11px] text-muted">
        <GearIcon width={11} height={11} />
        <span className="flex items-center gap-1" aria-hidden>
          <span className="size-1.5 rounded-full bg-[#2f9e62]" />
          <span className="size-1.5 rounded-full bg-[#2f9e62]" />
          <span className="size-1.5 rounded-full bg-[#2f9e62]" />
        </span>
        <UndoIcon width={11} height={11} />
        {mode !== 'chat' ? (
          <>
            <span className="num">− {mode === 'flow' ? '63' : '79'}% +</span>
            <span className="h-3 w-px bg-line" />
            <span className="num text-[#2f9e62]">60 fps</span>
            <span className="h-3 w-px bg-line" />
          </>
        ) : null}
        <span>{count}</span>
        <span className="h-3 w-px bg-line" />
        <span className="flex items-center gap-1.5">
          <WalletIcon width={11} height={11} />
          <span className="num">$24.00</span> Credits
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          <KeyboardIcon width={12} height={12} />
          <span className="num">⌘/</span>
        </span>
      </div>
    </div>
  )
}
